import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { BlLoadingService, BlFormGroup, BlValidatorService, Rules, BlModalService, ModalReason, BlDialogService } from '@blcloud/bl-ng-ui-component';
import { LoginPageService } from './login-page.service';
import { DeviceSessionService, LoginResourceService, ScreenService } from '@blcloud/bl-ng-share-module';
import { FaceDetectionModalComponent } from '../../feature/face-detection-modal/face-detection-modal.component';
import { IFaceDetectionModalRequest, IFaceDetectionModalResponse } from '../../feature/face-detection-modal/face-detection-modal.interface';
import { FaceDetectionModalMode } from '../../feature/face-detection-modal/face-detection-modal.enum';
import { BlAppContext } from '@blcloud/bl-ng-common';
import { defer as _defer } from 'lodash';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  providers: [LoginPageService]
})
export class LoginPageComponent implements OnInit {

  @Input() userId: string;
  @Input() password: string;

  message: string;
  logout: string;
  isInitCheck = false;
  returnUrl: string = null;

  /** 入力検証オブジェクト */
  form = new BlFormGroup();

  /** @ignore 顔認証有効 */
  _enableFaceDetection = false;

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private userLoginInfo: LoginResourceService,
    private loginPageService: LoginPageService,
    private loading: BlLoadingService,
    private deviceSessionSession: DeviceSessionService,
    private screenService: ScreenService,
    private blValidatorService: BlValidatorService,
    private blModalService: BlModalService,
    private appContext: BlAppContext,
    private blDialogService: BlDialogService
  ) {
    this.loginPageService.clearLoginCookie();
    this.title.setTitle('ログインページ');
  }

  /**
   * 初期化
   */
  ngOnInit() {
    // MEMO
    // 今はモックなのでIT環境でのみ動作するようにする
    if (!this.appContext.environment.production) {
      this._enableFaceDetection = true;
    }

    this.screenService.hideFrame();
    this.returnUrl = null;
    this.route.queryParams.subscribe(params => {
      if (params.logout !== undefined) {
        this.logout = 'ログアウトしました。';
      }
      if (params.timeout !== undefined) {
        this.logout = 'セッションの有効期限が切れました。';
      }
      if (params.error !== undefined) {
        this.logout = '認証情報の異常のため、ログアウトしました。';
      }
      if (
        params.rtnUrl !== undefined &&
        !params.rtnUrl.split('?')[0].match(/menu\/login$/) // ログイン画面へのリダイレクトは防止する
      ) {
        this.returnUrl = params.rtnUrl;
      }
    });
    this.loginPageService.clearLoginCookie();
    // 検証用のルールを取得
    this.loginPageService.rules().subscribe(
      (rules: Rules[]) => {
        this.form = this.blValidatorService.getBlFormGroup(rules);
      },
      () => {
        // ログイン画面にリダイレクトされる場合があるので新規エラーをスルーする。
        throw new Error('検証用ルールが取得できません。');
      }
    );
    // TODO: 直接背景を設定する方法がないため、selectorで上書き
    document.querySelector('body').style.backgroundColor = '#efefef';
  }

  /**
   * ログイン処理
   */
  onLogin() {

    // 入力値検証
    if (!this.form.validate()) {
      return;
    }

    this.setMessage('ログイン中');
    this.loading.show();
    this.logout = null;
    this.userLoginInfo.login(this.userId, this.password).subscribe(
    (res) => {
      this.loading.hide();
      // ログイン画面に遷移
      if (res instanceof Array) {
        this.loginPageService.setLoginCookie(res);
        // ログインできたら端末セッションの登録を行う
        try {
          this.deviceSessionSession.createDeviceSession().subscribe(
            success => {
              console.log(success);
              if (this.returnUrl) {
                window.open(this.returnUrl, '_self'); // 指定されたリダイレクト先に遷移
              } else {
                this.router.navigate(['biz']);
              }
            },
            err => console.log(err),
          );
        } catch (err) {
          console.log(err);
        }

      } else {
        // 汎用エラー画面に遷移
        throw new Error('配列ではありません。');
      }
    }, (_e) => {

      this.loading.hide();
      this.password = null;
      this.form.reset();

      if (_e.status === 401) {
        const result = _e.messages[0].split(',');
        if (result[0] === 'E00067') {
          // ログイン認証エラーメッセージを表示
          this.setMessage('ログインIDまたはパスワードが正しくありません。\nもう一度お試しください。');
        } else if (result[0] === 'E00544') {
          // アカウント該当無しエラーメッセージを表示
          this.setMessage('アカウントが見つかりませんでした。\nアカウントの作成が完了しているかご確認ください。');
        } else if (result[0] === 'E00142') {
          // リボーク時エラーメッセージを表示
          this.setMessage('パスワード認証に失敗したため、パスワードが無効となりました。パスワードの再設定を行ってください。');
        } else if (result[0] === 'E00543') {
          // アカウント無効エラーメッセージを表示
          this.setMessage('当該アカウントは、現在ご利用いただけません。');
        } else if (result[0] === 'E00545') {
          // アカウント制限エラーメッセージを表示
          this.setMessage('アクセスが制限されています。');
        } else {
          // 汎用エラー画面（認証エラーのダイアログ）に遷移
          throw _e;
        }
      } else {
        // 汎用エラー画面に遷移
        throw _e;
      }

    });
  }

  /**
   * chromeのダウンロードページへのリンク
   * @param event
   */
  onOpenChrome(_event: any) {
    window.open('https://www.google.co.jp/chrome/browser/desktop/index.html', '_blank');
  }

  setMessage(value: string) {
    this.message = value;
  }

  /**
   * パスワードNextFocus時処理
   * @param value 入力値
   */
  onPasswordInputEnter(event: KeyboardEvent) {
    const submit = <HTMLInputElement>document.querySelector('#submit');
    submit.focus();
    /**
     * TODO: 強制的にフォーカスアウトすると、clickも発火するのでコメントアウト。
     * 今後、BlFocusComponentの仕様が変わった際に動かなくなる可能性があるのでTODOを残す。
     */
    // submit.click();
    event.stopPropagation();
  }

  /**
   * 顔認証クリック検知
   * @param event イベント
   */
  onClickFaceDetection(event: MouseEvent) {
    const data: IFaceDetectionModalRequest = {
      mode: FaceDetectionModalMode.Auth,
      showAuxiliaryLine: true,
    };
    this.blModalService.show(FaceDetectionModalComponent, { data }).subscribe({
      next: (modalRef) => {
        let focusTarget = event.target;

        const response = modalRef.getResults() as IFaceDetectionModalResponse;
        if (modalRef.reason === ModalReason.Error) {
          focusTarget = null;

          this.blDialogService.warn('顔認証サービスにてエラーが発生しました。\nしばらくお待ち下さい').subscribe({
            next: dialogRef => dialogRef.hide(event.target)
          });
        } else if (modalRef.reason === ModalReason.Done) {
            // MEMO
            // なぜか@Inputになっているが、この画面を描画する親はいないのでPGミスかと
            // よって、該当変数への値の代入も許容する(@Inputで渡される値に内部でセットするのはよくない)
            this.userId = response.loginUser.loginId;
            this.password = response.loginUser.password;

            _defer(() => this.onLogin());
        }

        modalRef.hide(focusTarget);
      }
    });
  }
}




// WEBPACK FOOTER //
// ./src/app/page/login-page/login-page.component.ts